import React, { useState } from 'react';
import { X, Trash2, AlertCircle } from 'lucide-react';
import { Miembro } from '../lib/supabase';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { DialogShell } from './ui/DialogShell';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';

interface HardDeleteMiembroModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  miembro: Miembro | null;
}

export const HardDeleteMiembroModal: React.FC<HardDeleteMiembroModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  miembro
}) => {
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const { error, clearError, handleError } = useErrorHandler();

  const resetAndClose = () => {
    if (!loading) {
      setConfirmChecked(false);
      handleError('');
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleError('');
    if (!confirmChecked) {
      handleError('Debes marcar la casilla de confirmación');
      return;
    }
    try {
      setLoading(true);
      await onConfirm();
      setConfirmChecked(false);
      resetAndClose();
    } catch (err) {
      reportarErrorInesperado(err, 'No pudimos eliminar el miembro.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogShell
      isOpen={isOpen && !!miembro}
      onClose={resetAndClose}
      onBeforeClose={() => false}
      size="sm"
      header={
        <div className="bg-red-600 text-white p-6 flex items-center justify-between rounded-t-2xl sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Trash2 className="w-6 h-6" />
            <h2 className="text-xl font-bold">Eliminación Permanente</h2>
          </div>
          <button
            onClick={resetAndClose}
            disabled={loading}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
          <div className="flex items-start gap-3 mb-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-red-900 mb-2">
                ¡ADVERTENCIA! Esta acción es IRREVERSIBLE
              </p>
              <p className="text-sm text-red-800">
                Estás a punto de eliminar permanentemente a{' '}
                <span className="font-bold">{miembro?.nombres} {miembro?.apellidos}</span>.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3 text-sm text-slate-700">
          <p className="font-semibold text-slate-800">Se eliminarán permanentemente:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Todos los datos personales del miembro</li>
            <li>Historial de cambios de estado</li>
            <li>Referencias en traslados</li>
            <li>Toda información relacionada</li>
          </ul>
          <p className="font-semibold text-red-600 mt-4">
            Esta opción solo debe usarse en casos excepcionales. Se recomienda usar "Marcar Sin Derechos" en su lugar.
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              disabled={loading}
              className="mt-1 w-5 h-5 text-red-600 border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
            />
            <span className="text-sm text-slate-700">
              Comprendo que esta acción es irreversible y que se eliminarán todos los datos del miembro de
              forma permanente. Confirmo que tengo autorización para realizar esta acción.
            </span>
          </label>
        </div>

        <FormErrorAlert error={error} onClear={clearError} />

        <div className="flex gap-3">
          <button
            type="button"
            onClick={resetAndClose}
            disabled={loading}
            className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || !confirmChecked}
            className="flex-1 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Eliminando...' : 'Eliminar Permanentemente'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
};
